
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, IconButton } from '../ui-kitNew/buttonNew'
import { Segmented } from '../ui-kitNew/controlsNew'
import { Select } from '../ui-kitNew/selectNew'
import { EmptyState } from '../ui-kitNew/cardNew'
import { FilterIcon, PlusIcon, TrashIcon } from '../ui-kitNew/iconsNew'
import { DIMENSION_FIELDS, FIELDS, distinctValues, groupFields, resolveField } from '../../lib/reportNew/fieldsNew'
import { createCondition, defaultOperator, getFieldValue, operatorMeta, operatorsForField } from '../../lib/reportNew/filtersNew'
import { formatDate, formatFieldValue } from '../../lib/reportNew/formatNew'
import { ValueInput } from './value-inputNew'

/** Opciones de valor de un campo, calculadas de los datos reales. */
export function useFieldOptions(rows, fieldKey, fields) {
  const { t } = useTranslation()
  return useMemo(() => {
    const field = resolveField(fieldKey, fields)
    if (!field) return []
    if (field.type === 'boolean') {
      return [
        { value: 'true', label: t('common.yes') },
        { value: 'false', label: t('common.no') },
      ]
    }
    return distinctValues(rows, fieldKey, fields).map((value) => ({
      value,
      label:
        Array.isArray(field.labelMap) && field.labelMap[value] !== undefined
          ? field.labelMap[value]
          : field.type === 'date'
            ? formatDate(value)
            : String(formatFieldValue(fieldKey, value)),
    }))
  }, [rows, fieldKey, fields, t])
}

function ConditionRow({ rows, condition, index, logic, onChange, onRemove, fields }) {
  const { t } = useTranslation()
  const options = useFieldOptions(rows, condition.field, fields)
  const operators = operatorsForField(condition.field, fields)
  const meta = operatorMeta(condition.field, condition.operator, fields)

  const fieldOptions = useMemo(
    () =>
      groupFields(fields).flatMap((group) =>
        group.fields.map((field) => ({ value: field.key, label: field.label, group: group.name }))
      ),
    [fields]
  )

  const changeField = (fieldKey) => {
    const operator = defaultOperator(fieldKey, fields)
    const nextMeta = operatorMeta(fieldKey, operator, fields)
    onChange({
      ...condition,
      field: fieldKey,
      operator,
      value: nextMeta?.input === 'multi' ? [] : '',
      value2: '',
    })
  }

  const changeOperator = (operator) => {
    const nextMeta = operatorMeta(condition.field, operator, fields)
    const keepValue =
      nextMeta?.input === meta?.input ? condition.value : nextMeta?.input === 'multi' ? [] : ''
    onChange({ ...condition, operator, value: keepValue, value2: '' })
  }

  return (
    <li className="flex flex-col gap-3 rounded-xl border border-border bg-muted/40 p-2.5 sm:flex-row sm:items-start">
      <span className="w-14 shrink-0 pt-1.5 text-[11px] font-semibold tracking-wide whitespace-nowrap text-muted-foreground uppercase">
        {index === 0 ? t('ui.filters.where') : logic === 'OR' ? t('ui.filters.logicOr') : t('ui.filters.logicAnd')}
      </span>

      <div className="grid flex-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)]">
        <Select
          size="sm"
          searchable
          options={fieldOptions}
          value={condition.field}
          onChange={changeField}
          ariaLabel={t('ui.filters.fieldAria')}
        />
        <Select
          size="sm"
          options={operators.map((operator) => ({ value: operator.value, label: operator.label }))}
          value={condition.operator}
          onChange={changeOperator}
          ariaLabel={t('ui.filters.operatorAria')}
        />
        <div className={`min-w-0 ${meta?.input === 'none' ? 'hidden sm:block' : ''}`}>
          <ValueInput condition={condition} onChange={onChange} options={options} fields={fields} />
        </div>
      </div>

      <IconButton label={t('ui.filters.removeCondition')} onClick={onRemove} className="hover:text-destructive">
        <TrashIcon size={16} />
      </IconButton>
    </li>
  )
}

/**
 * Constructor de filtros reutilizable: se usa en el panel global, en cada
 * widget del informe y en el menú avanzado de las columnas de la tabla.
 */
export function FilterBuilder({
  rows,
  group,
  onChange,
  fields = FIELDS,
  defaultField = DIMENSION_FIELDS[0]?.key,
  compact = false,
}) {
  const { t } = useTranslation()
  const conditions = group?.conditions || []
  const logic = group?.logic || 'AND'

  const update = (patch) => onChange({ logic, conditions, ...patch })

  const addCondition = () => {
    update({ conditions: [...conditions, createCondition(defaultField)] })
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-muted-foreground">{t('ui.filters.combineWith')}</span>
          <Segmented
            ariaLabel={t('ui.filters.logicAria')}
            value={logic}
            onChange={(value) => update({ logic: value })}
            options={[
              { value: 'AND', label: t('ui.filters.allConditions') },
              { value: 'OR', label: t('ui.filters.anyCondition') },
            ]}
          />
        </div>
        <div className="flex items-center gap-1">
          {conditions.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => update({ conditions: [] })}>
              {t('ui.filters.clearConditions')}
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={addCondition}>
            <PlusIcon size={15} />
            {t('ui.filters.addCondition')}
          </Button>
        </div>
      </div>

      {conditions.length === 0 ? (
        <EmptyState
          className={compact ? 'py-6' : undefined}
          icon={<FilterIcon size={22} />}
          title={t('ui.filters.noConditions')}
          description={t('ui.filters.noConditionsHelp')}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {conditions.map((condition, index) => (
            <ConditionRow
              key={condition.id}
              rows={rows}
              fields={fields}
              condition={condition}
              index={index}
              logic={logic}
              onChange={(next) =>
                update({
                  conditions: conditions.map((item) => (item.id === next.id ? next : item)),
                })
              }
              onRemove={() =>
                update({ conditions: conditions.filter((item) => item.id !== condition.id) })
              }
            />
          ))}
        </ul>
      )}
    </div>
  )
}
